import { useQuery, useMutation } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { Download } from 'lucide-react';
import { type TelemetryPassportClientResponse } from '@siliconx/shared';
import { PassportBody } from '@siliconx/shared/ui/AssetPassportBody';
import { api, throwIfNotOk } from '../../lib/api';
import type { DownloadUrlResponse } from '../../lib/types';
import { formatDate } from '../../lib/format';
import { LoadingShell } from '@siliconx/shared/ui/state';
import { qk } from '../../lib/queryKeys';
import { Card } from '@siliconx/shared/ui/Card';
import { DataTable } from '@siliconx/shared/ui/DataTable';

/**
 * Shared digital-passport presentation (RUF-555, extracted). The §1-§5 stack
 * now lives in @siliconx/shared/ui/AssetPassportBody so the ops portal renders
 * the identical document; this file keeps the customer-only issued-artifacts
 * card (generate/download go through /api/client/*, which only exists for
 * customer callers). SIL-737 removed the self-fetching embed — every surface
 * now goes through <PassportSurface>, which owns the fetch.
 *
 * FMV is stripped server-side for non-owners (assemblePassportForAsset), so
 * the disclaimer's FMV line simply never renders for a buyer — no
 * context-specific logic here.
 */

interface ApiError extends Error {
  status?: number;
}

// ---------------------------------------------------------------------------
// Issued artifacts — prior on-demand generated PDF artifacts for this asset.
// ---------------------------------------------------------------------------

interface IssuedArtifact {
  id: string;
  artifactId: string;
  generatedAt: string;
  transactionId: string | null;
}

interface IssuedArtifactsResponse {
  data: IssuedArtifact[];
}

function issuedArtifactColumns(fingerprint: string): ColumnDef<IssuedArtifact, unknown>[] {
  return [
    {
      id: 'artifactId',
      header: 'Code',
      cell: (ctx) => (
        <span className="font-num text-zinc-800">{ctx.row.original.artifactId}</span>
      ),
    },
    {
      id: 'generatedAt',
      header: 'Generated',
      cell: (ctx) => (
        <span className="text-zinc-600">{formatDate(ctx.row.original.generatedAt)}</span>
      ),
    },
    {
      id: 'download',
      header: () => <span className="sr-only print:hidden">Download</span>,
      cell: (ctx) => (
        <span className="print:hidden">
          <ArtifactDownloadButton fingerprint={fingerprint} artifactId={ctx.row.original.artifactId} />
        </span>
      ),
    },
  ];
}

function IssuedArtifactsCard({ fingerprint }: { fingerprint: string }) {
  const { data, isLoading } = useQuery<IssuedArtifactsResponse, ApiError>({
    queryKey: qk.assets.passportArtifacts(fingerprint),
    queryFn: async () => {
      const res = await api.api.client.passport[':fingerprint'].artifacts.$get({
        param: { fingerprint },
      });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as IssuedArtifactsResponse;
    },
  });

  const rows = data?.data ?? [];

  return (
    <Card className="print:border-zinc-300">
      <h2 className="text-base font-semibold text-zinc-900">Issued artifacts</h2>
      <p className="mt-1 text-sm text-zinc-500 print:hidden">
        Stamped, hashed PDF artifacts generated on-demand for this asset — each is an
        immutable, independently verifiable record (distinct from Print / Save as PDF above).
      </p>

      {isLoading ? (
        <div className="mt-4 print:hidden">
          <LoadingShell rows={2} />
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-4 text-sm text-zinc-400">No artifacts issued yet.</p>
      ) : (
        // stack={false}: this card renders on the print/PDF passport page
        // (passport.$fingerprint.tsx, window.print()), where DataTable's
        // below-640px stacked-card mode (hidden sm:block / sm:hidden) is a
        // real risk — print layout width isn't guaranteed to satisfy the sm
        // breakpoint. Keep it a plain scrolling table on all viewports.
        <div className="mt-4">
          <DataTable
            data={rows}
            columns={issuedArtifactColumns(fingerprint)}
            stack={false}
          />
        </div>
      )}
    </Card>
  );
}

function ArtifactDownloadButton({ fingerprint, artifactId }: { fingerprint: string; artifactId: string }) {
  const mutation = useMutation<DownloadUrlResponse, ApiError, void>({
    mutationFn: async () => {
      const res = await api.api.client.passport[':fingerprint'].artifacts[
        ':artifactId'
      ].download.$get({ param: { fingerprint, artifactId } });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as DownloadUrlResponse;
    },
    onSuccess: (data) => {
      window.open(data.downloadUrl, '_blank', 'noopener,noreferrer');
    },
  });

  return (
    <button
      type="button"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className="inline-flex items-center gap-1 text-xs font-medium text-zinc-600 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-60"
    >
      <Download className="size-3.5" /> {mutation.isPending ? 'Loading…' : 'Download'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Presentational body — the §1-§5 stack, reused by every surface.
// ---------------------------------------------------------------------------

export function AssetPassportBody({
  passport,
  fingerprint,
  showArtifacts = false,
}: {
  passport: TelemetryPassportClientResponse;
  fingerprint: string;
  showArtifacts?: boolean;
}) {
  return (
    <>
      {/* §1-§5 stack — shared with the ops portal (RUF-556 extraction continued). */}
      <PassportBody passport={passport} />
      {showArtifacts && <IssuedArtifactsCard fingerprint={fingerprint} />}
    </>
  );
}
